import { useState, useEffect, useCallback } from 'react';
import { GameState, Direction, Grid } from '../types/game';
import {
    createEmptyGrid,
    addRandomTile,
    moveGrid,
    isGameOver,
    hasWon,
    GRID_SIZE
} from '../utils/gameLogic';

const CHEAT_CODE = [
    'ArrowUp',
    'ArrowUp',
    'ArrowDown',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowLeft',
    'ArrowRight',
    'KeyB',
    'KeyA',
    'KeyB',
    'KeyA'
];

export function useGame() {
    const [gameState, setGameState] = useState<GameState>({
        grid: createEmptyGrid(),
        score: 0,
        gameOver: false,
        won: false,
    });

    const [keySequence, setKeySequence] = useState<string[]>([]);

    const initializeGame = useCallback(() => {
        let newGrid = createEmptyGrid();
        // Add two initial tiles
        newGrid = addRandomTile(newGrid);
        newGrid = addRandomTile(newGrid);

        setGameState({
            grid: newGrid,
            score: 0,
            gameOver: false,
            won: false,
        });
        setKeySequence([]);
    }, []);

    // Auto-start the game
    useEffect(() => {
        initializeGame();
    }, [initializeGame]);

    const activateCheat = useCallback(() => {
        const cheatGrid: Grid = Array(GRID_SIZE).fill(null)
            .map(() => Array(GRID_SIZE).fill(-1));

        setGameState({
            grid: cheatGrid,
            score: -2048,
            gameOver: true,
            won: false,
        });
    }, []);

    const move = useCallback((direction: Direction) => {
        if (gameState.gameOver) return;

        const { grid: newGrid, score: moveScore } = moveGrid(gameState.grid, direction);
        const hasChanged = JSON.stringify(newGrid) !== JSON.stringify(gameState.grid);

        if (hasChanged) {
            const gridWithNewTile = addRandomTile(newGrid);
            const newGameOver = isGameOver(gridWithNewTile);
            const newWon = hasWon(gridWithNewTile);

            setGameState(prev => ({
                grid: gridWithNewTile,
                score: prev.score + moveScore,
                gameOver: newGameOver,
                won: newWon || prev.won,
            }));
        }
    }, [gameState]);

    const handleKeyPress = useCallback((event: KeyboardEvent) => {
        // Update key sequence
        setKeySequence(prev => {
            const newSequence = [...prev, event.code];
            // Keep only the last N keys where N is the length of the cheat code
            if (newSequence.length > CHEAT_CODE.length) {
                return newSequence.slice(-CHEAT_CODE.length);
            }
            return newSequence;
        });

        const keyToDirection: { [key: string]: Direction } = {
            'ArrowUp': 'up',
            'ArrowDown': 'down',
            'ArrowLeft': 'left',
            'ArrowRight': 'right',
        };

        const direction = keyToDirection[event.key];
        if (direction) {
            event.preventDefault();
            move(direction);
        }
    }, [move]);

    // Check for cheat code
    useEffect(() => {
        if (keySequence.length === CHEAT_CODE.length &&
            keySequence.every((key, i) => key === CHEAT_CODE[i])) {
            activateCheat();
        }
    }, [keySequence, activateCheat]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);

    return {
        grid: gameState.grid,
        score: gameState.score,
        gameOver: gameState.gameOver,
        won: gameState.won,
        move,
        initializeGame,
    };
} 